import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { motion } from 'framer-motion';
import { FrontendTournament } from '../../../../../../corelogic/domain/models/tournament.model';
import PulsingActiveIcon from '../../Shared/PulsingActiveIcon/PulsingActiveIcon';
import TournamentListCard from './TournamentListCard';

const styles = {
  container: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    padding: { xs: 1, sm: 2 },
    bgcolor: 'transparent',
    height: '100%',
    minHeight: {
      xs: '450px',
      sm: '480px',
      md: '400px',
      lg: '600px',
      xl: '700px',
    },
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background:
        'linear-gradient(145deg, rgba(40, 40, 40, 0.2), rgba(30, 30, 30, 0))',
      borderRadius: 2,
      zIndex: -1,
    },
  },
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    mb: 3,
    width: '100%',
  },
  sectionTitle: {
    fontFamily: 'Rubik Mono One',
    color: '#ffffff',
    fontSize: 'clamp(1.2rem, 1.7vw, 1.8rem)',
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: -8,
      left: 0,
      width: '40px',
      height: '3px',
      backgroundColor: '#cc0000',
      borderRadius: '2px',
    },
  },
  controlsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    gap: 2,
    mb: 0.5,
    flexWrap: 'wrap',
  },
  buttonsContainer: {
    display: 'flex',
    gap: 1.5,
    justifyContent: 'flex-end',
  },
  actionButton: {
    color: '#ffffff',
    borderColor: 'rgba(204, 0, 0, 0.7)',
    borderWidth: '1px',
    backgroundColor: 'rgba(40, 40, 40, 0.8)',
    height: {
      xs: '32px',
      sm: '34px',
      md: '36px',
      lg: '40px',
    },
    fontSize: 'clamp(0.7rem, 0.9vw, 0.95rem)',
    lineHeight: 1.2,
    borderRadius: '4px',
    textTransform: 'none',
    fontWeight: 'medium',
    transition: 'all 0.2s ease-in-out',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
    '&:hover': {
      borderColor: '#ff0000',
      backgroundColor: 'rgba(204, 0, 0, 0.15)',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.5)',
    },
    '& .MuiButton-startIcon': {
      marginRight: { xs: '2px', sm: '4px', md: '4px', lg: '6px' },
      '& .MuiSvgIcon-root': {
        fontSize: 'clamp(0.9rem, 1vw, 1.1rem)',
      },
    },
  },
  filtersContainer: {
    pt: 1.5,
    borderTop: '2px solid rgba(204, 0, 0, 0.3)',
    backgroundColor: 'rgba(20, 20, 20, 0.3)',
    borderRadius: '4px',
  },
  toggleButtonGroup: {
    '& .MuiToggleButtonGroup-grouped': {
      border: '1px solid rgba(204, 0, 0, 0.4)',
      color: '#cccccc',
      backgroundColor: 'rgba(30, 30, 30, 0.7)',
      height: {
        xs: '26px',
        sm: '28px',
        md: '30px',
        lg: '32px',
      },
      px: 1,
      mb: 1,
      fontWeight: 'medium',
      '& .MuiSvgIcon-root': {
        fontSize: '1rem',
      },
      '&.Mui-selected': {
        backgroundColor: 'rgba(204, 0, 0, 0.2)',
        color: '#ffffff',
        fontWeight: 'bold',
        '&:hover': {
          backgroundColor: 'rgba(204, 0, 0, 0.25)',
        },
      },
      '&:hover': {
        backgroundColor: 'rgba(50, 50, 50, 0.7)',
      },
      '&:not(:first-of-type)': {
        borderLeft: '1px solid rgba(204, 0, 0, 0.4)',
      },
    },
  },
  toggleButtonText: {
    fontSize: 'clamp(0.7rem, 0.9vw, 0.95rem)',
  },
  listContainer: {
    flex: 1,
    overflowY: 'auto',
    bgcolor: 'rgba(20, 20, 20, 0.7)',
    borderRadius: 2,
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
    padding: 1.5,
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(255, 255, 255, 0.3)',
      borderRadius: '4px',
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.5)',
      },
    },
  },
  emptyState: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    flexDirection: 'column',
    gap: 2,
    color: '#cccccc',
  },
  emptyStateTitle: {
    fontFamily: 'Rubik Mono One',
    color: '#ffffff',
    fontSize: 'clamp(1rem, 1.5vw, 1.3rem)',
  },
  emptyStateText: {
    color: '#cccccc',
    fontSize: 'clamp(0.7rem, 1vw, 0.8rem)',
  },
  listVariants: {
    hidden: {
      opacity: 0,
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  },
  itemVariants: {
    hidden: {
      opacity: 0,
      y: 20,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
      },
    },
  },
  cardRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: {
      xs: 1.2,
      sm: 1.3,
      md: 1.5,
      lg: 1.7,
    },
    paddingRight: 1,
    '& > div': {
      transition: 'transform 0.2s ease-in-out',
      '&:hover': {
        transform: 'translateX(4px)',
      },
    },
  },
};

interface TournamentListProps {
  tournaments: FrontendTournament[];
  onTournamentSelect: (tournament: FrontendTournament) => void;
  selectedTournamentId: string | null;
  tournamentType: 'all' | 'public' | 'private';
  onTournamentTypeChange: (type: 'all' | 'public' | 'private') => void;
  viewType: 'active' | 'all' | 'completed';
  onViewTypeChange: (type: 'active' | 'all' | 'completed') => void;
  onCreateTournament: () => void;
  onSearchTournament: () => void;
}

const TournamentList = ({
  tournaments,
  onTournamentSelect,
  selectedTournamentId,
  tournamentType,
  onTournamentTypeChange,
  viewType,
  onViewTypeChange,
  onCreateTournament,
  onSearchTournament,
}: TournamentListProps) => {
  const handleTournamentTypeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newTournamentType: 'all' | 'public' | 'private' | null,
  ) => {
    if (_event) {
      _event.stopPropagation();
    }

    if (newTournamentType !== null) {
      onTournamentTypeChange(newTournamentType);
    }
  };

  const handleViewTypeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newViewType: 'all' | 'active' | 'completed' | null,
  ) => {
    if (_event) {
      _event.stopPropagation();
    }

    if (newViewType !== null) {
      onViewTypeChange(newViewType);
    }
  };

  return (
    <Box sx={styles.container}>
      {/* Header with title and action buttons */}
      <Box sx={styles.headerContainer}>
        <Typography sx={styles.sectionTitle}>Your Tournaments</Typography>

        <Box sx={styles.buttonsContainer}>
          <Button
            variant="outlined"
            startIcon={<SearchIcon />}
            sx={styles.actionButton}
            onClick={onSearchTournament}
          >
            Find New
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            sx={styles.actionButton}
            onClick={onCreateTournament}
          >
            Create New
          </Button>
        </Box>
      </Box>

      {/* Tournament Type Filter */}
      <Box sx={styles.filtersContainer}>
        <Box sx={styles.controlsContainer}>
          <ToggleButtonGroup
            value={tournamentType}
            exclusive
            onChange={handleTournamentTypeChange}
            aria-label="tournament type"
            size="small"
            sx={styles.toggleButtonGroup}
          >
            <ToggleButton value="all">
              <Typography variant="body2" sx={styles.toggleButtonText}>
                ALL
              </Typography>
            </ToggleButton>
            <ToggleButton value="public">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                <PublicIcon fontSize="small" />
                <Typography variant="body2" sx={styles.toggleButtonText}>
                  PUBLIC
                </Typography>
              </Box>
            </ToggleButton>
            <ToggleButton value="private">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                <LockIcon fontSize="small" />
                <Typography variant="body2" sx={styles.toggleButtonText}>
                  PRIVATE
                </Typography>
              </Box>
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Active/All Toggle */}
          <ToggleButtonGroup
            value={viewType}
            exclusive
            onChange={handleViewTypeChange}
            aria-label="view type"
            size="small"
            sx={styles.toggleButtonGroup}
          >
            <ToggleButton value="all">
              <Typography variant="body2" sx={styles.toggleButtonText}>
                ALL
              </Typography>
            </ToggleButton>
            <ToggleButton value="active">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                <PulsingActiveIcon />
                <Typography variant="body2" sx={styles.toggleButtonText}>
                  ACTIVE
                </Typography>
              </Box>
            </ToggleButton>
            <ToggleButton value="completed">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                <CheckCircleIcon fontSize="small" />
                <Typography variant="body2" sx={styles.toggleButtonText}>
                  COMPLETED
                </Typography>
              </Box>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Tournament List */}
      {tournaments.length > 0 && (
        <Box
          component={motion.div}
          initial="hidden"
          animate="visible"
          variants={styles.listVariants}
          sx={styles.listContainer}
        >
          <Box sx={styles.cardRow}>
            {tournaments.map((tournament) => (
              <motion.div key={tournament.id} variants={styles.itemVariants}>
                <TournamentListCard
                  tournament={tournament}
                  isSelected={tournament.id === selectedTournamentId}
                  onClick={() => onTournamentSelect(tournament)}
                />
              </motion.div>
            ))}
          </Box>
        </Box>
      )}
      {/* Empty State */}
      {tournaments.length === 0 && (
        <Box sx={styles.emptyState}>
          <EmojiEventsIcon
            sx={{ fontSize: 60, color: '#cc0000', opacity: 0.7 }}
          />
          <Typography sx={styles.emptyStateTitle}>No Tournaments</Typography>
          <Typography sx={styles.emptyStateText}>
            Create or join tournaments to see them here
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default TournamentList;
